import {
  Box,
  Button,
  HStack,
  IconButton,
  Image,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FiEdit2 } from 'react-icons/fi';
import { useQueryClient } from 'react-query';

import {
  FuelCalibration,
  FuelReading,
  FuelReceipt,
  FuelReceiptCheck,
  FuelRecordKind,
  fullPhotoUrl,
  fuelReadingsService,
} from '../../../services/fuelReadingsService';
import {
  SLOT_LABEL,
  formatDateTime,
  formatDay,
  formatDeviation,
  formatLiters,
  formatReading,
} from '../../../utils/fuelReadings';
import { AttachPhotoButton } from './AttachPhotoButton';
import { CancelDialog } from './CancelDialog';
import { EditReadingDialog } from './EditReadingDialog';

// foto pequena (clica e abre). sem foto: etiqueta e, se ainda dá, o botão de
// anexar depois. anexada depois: avisa quem e quando.
function Thumb({
  item,
  kind,
  alt,
  onClick,
  canAttach = true,
  desktop = false,
}: {
  item: {
    id: string;
    photo_url: string | null;
    photo_attached_at: string | null;
    photo_attached_by_name: string | null;
    canceled_at: string | null;
  };
  kind: FuelRecordKind;
  alt: string;
  onClick: () => void;
  canAttach?: boolean; // liberação não tem leitura para comprovar
  desktop?: boolean; // lançada no pc do posto: entra sempre sem foto
}) {
  if (!item.photo_url) {
    return (
      <>
        <Tag
          size="sm"
          colorScheme={desktop ? 'orange' : 'gray'}
          title={
            desktop
              ? 'Lançada no PC do posto: entra sem foto, dá para anexar depois'
              : undefined
          }
        >
          sem foto
        </Tag>
        {canAttach && !item.canceled_at && (
          <Box mt="1">
            <AttachPhotoButton kind={kind} id={item.id} label="Anexar" />
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      <Image
        src={fullPhotoUrl(item.photo_url) || ''}
        alt={alt}
        boxSize="44px"
        objectFit="cover"
        borderRadius="6px"
        cursor="pointer"
        onClick={onClick}
      />
      {item.photo_attached_at && (
        <Text
          fontSize="xs"
          color="gray.500"
          whiteSpace="nowrap"
          title={`Foto anexada depois por ${
            item.photo_attached_by_name || '—'
          } em ${formatDateTime(item.photo_attached_at, true)}`}
        >
          anexada {formatDateTime(item.photo_attached_at)}
        </Text>
      )}
    </>
  );
}

function Canceled({
  item,
}: {
  item: {
    canceled_at: string | null;
    canceled_by_name: string | null;
    cancel_reason: string | null;
  };
}) {
  return (
    <Tooltip
      bg="gray.650"
      label={`Anulada por ${item.canceled_by_name || '—'} em ${formatDateTime(
        item.canceled_at,
        true,
      )}: ${item.cancel_reason || ''}`}
    >
      <Tag size="sm" colorScheme="red">
        anulada
      </Tag>
    </Tooltip>
  );
}

// histórico das leituras do encerrante, com foto, correção e anulação
export function ReadingsHistory({
  readings,
  onPhoto,
  canEdit = false,
}: {
  readings: FuelReading[];
  onPhoto: (reading: FuelReading) => void;
  canEdit?: boolean; // supervisor ou ti: corrige o número (lápis)
}) {
  const queryClient = useQueryClient();
  const [canceling, setCanceling] = useState<FuelReading | null>(null);
  const [editing, setEditing] = useState<FuelReading | null>(null);

  if (readings.length === 0) {
    return <Text color="gray.500">Nenhuma leitura no mês.</Text>;
  }

  const cancel = async (reason: string) => {
    await fuelReadingsService.cancelReading(
      (canceling as FuelReading).id,
      reason,
    );
    await queryClient.invalidateQueries('fuel-readings');
  };

  const edit = async (data: {
    reading: string;
    reason: string;
    confirm_high_jump?: boolean;
  }) => {
    await fuelReadingsService.editReading((editing as FuelReading).id, data);
    await queryClient.invalidateQueries('fuel-readings');
  };

  return (
    <Box overflowX="auto" bg="gray.800" borderRadius="12px" p="2">
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Data e hora</Th>
            <Th>Turno</Th>
            <Th>Bomba</Th>
            <Th isNumeric>Totalizador</Th>
            <Th isNumeric>Anterior</Th>
            <Th isNumeric>Litros</Th>
            <Th>Lançado por</Th>
            <Th>Foto</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {readings.map(item => (
            <Tr key={item.id} opacity={item.canceled_at ? 0.55 : 1}>
              <Td whiteSpace="nowrap">{formatDateTime(item.read_at, true)}</Td>
              <Td whiteSpace="nowrap">{SLOT_LABEL[item.slot]}</Td>
              <Td>{item.pump_name}</Td>
              <Td
                isNumeric
                fontFamily="mono"
                textDecoration={item.canceled_at ? 'line-through' : 'none'}
              >
                {item.reading === null ? (
                  <Tag size="sm" colorScheme="blue">
                    Liberado
                  </Tag>
                ) : (
                  formatReading(item.reading)
                )}
                {item.edited_at && (
                  <Tooltip
                    bg="gray.650"
                    label={`Lançado ${formatReading(
                      item.original_reading,
                    )}. Corrigido por ${
                      item.edited_by_name || '—'
                    } em ${formatDateTime(item.edited_at, true)}: ${
                      item.edit_reason || ''
                    }`}
                  >
                    <Tag size="sm" colorScheme="purple" ml="2">
                      corrigida
                    </Tag>
                  </Tooltip>
                )}
              </Td>
              <Td isNumeric fontFamily="mono" color="gray.500">
                {formatReading(item.previous_reading)}
              </Td>
              <Td isNumeric>
                {formatLiters(item.liters)}
                {item.high_jump_confirmed && (
                  <Tooltip
                    bg="gray.650"
                    label="Acima do limite, confirmado por quem lançou"
                  >
                    <Tag size="sm" colorScheme="orange" ml="2">
                      confirmado
                    </Tag>
                  </Tooltip>
                )}
              </Td>
              <Td>
                {item.access_name ||
                  (item.source === 'desktop' ? 'PC do posto' : '—')}
                {item.notes && (
                  <Text fontSize="xs" color="gray.500">
                    {item.notes}
                  </Text>
                )}
              </Td>
              <Td>
                <Thumb
                  item={item}
                  kind="reading"
                  alt={`Foto da leitura de ${formatDateTime(item.read_at)}`}
                  canAttach={item.reading !== null}
                  desktop={item.source === 'desktop'}
                  onClick={() => onPhoto(item)}
                />
              </Td>
              <Td>
                {item.canceled_at ? (
                  <Canceled item={item} />
                ) : (
                  <HStack spacing="2">
                    {canEdit && item.reading !== null && (
                      <IconButton
                        aria-label="Corrigir o número"
                        title="Corrigir o número"
                        icon={<FiEdit2 />}
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => setEditing(item)}
                      />
                    )}
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="red"
                      onClick={() => setCanceling(item)}
                    >
                      Anular
                    </Button>
                  </HStack>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <EditReadingDialog
        reading={editing}
        onClose={() => setEditing(null)}
        onConfirm={edit}
      />

      <CancelDialog
        title="Anular leitura"
        isOpen={!!canceling}
        onClose={() => setCanceling(null)}
        onConfirm={cancel}
      >
        {canceling && (
          <Text>
            {SLOT_LABEL[canceling.slot]} da {canceling.pump_name} em{' '}
            {formatDateTime(canceling.read_at, true)}:{' '}
            <b>{formatReading(canceling.reading)}</b>, lançada por{' '}
            {canceling.access_name || '—'}.
          </Text>
        )}
      </CancelDialog>
    </Box>
  );
}

// histórico das aferições (medida padrão de 20 l)
export function CalibrationsHistory({
  calibrations,
  onPhoto,
}: {
  calibrations: FuelCalibration[];
  onPhoto: (calibration: FuelCalibration) => void;
}) {
  const queryClient = useQueryClient();
  const [canceling, setCanceling] = useState<FuelCalibration | null>(null);

  if (calibrations.length === 0) {
    return (
      <Text color="gray.500">
        Nenhuma aferição no mês. O procedimento pede pelo menos uma por mês em
        cada bomba.
      </Text>
    );
  }

  const cancel = async (reason: string) => {
    await fuelReadingsService.cancelCalibration(
      (canceling as FuelCalibration).id,
      reason,
    );
    await queryClient.invalidateQueries('fuel-readings');
  };

  return (
    <Box overflowX="auto" bg="gray.800" borderRadius="12px" p="2">
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Data e hora</Th>
            <Th>Bomba</Th>
            <Th isNumeric>Medida</Th>
            <Th isNumeric>Régua</Th>
            <Th>Resultado</Th>
            <Th>Lançado por</Th>
            <Th>Foto</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {calibrations.map(item => (
            <Tr key={item.id} opacity={item.canceled_at ? 0.55 : 1}>
              <Td whiteSpace="nowrap">
                {formatDateTime(item.calibrated_at, true)}
              </Td>
              <Td>{item.pump_name}</Td>
              <Td isNumeric>{formatLiters(item.volume_liters)} L</Td>
              <Td isNumeric fontFamily="mono">
                {formatDeviation(item.deviation_ml)}
              </Td>
              <Td>
                <Tag size="sm" colorScheme={item.approved ? 'green' : 'red'}>
                  {item.approved ? 'aprovada' : 'reprovada'}
                </Tag>
              </Td>
              <Td>
                {item.access_name || '—'}
                {item.notes && (
                  <Text fontSize="xs" color="gray.500">
                    {item.notes}
                  </Text>
                )}
              </Td>
              <Td>
                <Thumb
                  item={item}
                  kind="calibration"
                  alt={`Foto da aferição de ${formatDateTime(
                    item.calibrated_at,
                  )}`}
                  onClick={() => onPhoto(item)}
                />
              </Td>
              <Td>
                {item.canceled_at ? (
                  <Canceled item={item} />
                ) : (
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="red"
                    onClick={() => setCanceling(item)}
                  >
                    Anular
                  </Button>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <CancelDialog
        title="Anular aferição"
        isOpen={!!canceling}
        onClose={() => setCanceling(null)}
        onConfirm={cancel}
      >
        {canceling && (
          <Text>
            Aferição da {canceling.pump_name} em{' '}
            {formatDateTime(canceling.calibrated_at, true)}: régua{' '}
            <b>{formatDeviation(canceling.deviation_ml)}</b>.
          </Text>
        )}
      </CancelDialog>
    </Box>
  );
}

// situação da descarga no gescooper
function ReceiptStatus({
  check,
  erpAvailable,
}: {
  check: FuelReceiptCheck | undefined;
  erpAvailable: boolean;
}) {
  if (!erpAvailable || !check) {
    return <Text color="gray.500">—</Text>;
  }

  if (check.erp_day === null) {
    return (
      <Tag size="sm" colorScheme="orange">
        sem entrada no GesCooper
      </Tag>
    );
  }

  const liters =
    check.erp_liters !== null && Math.abs(check.erp_liters - check.liters) > 1;

  return (
    <>
      <Tag size="sm" colorScheme={check.late_days ? 'yellow' : 'green'}>
        entrou {formatDay(check.erp_day).slice(0, 5)}
        {check.late_days ? ` (${check.late_days} dia(s) depois)` : ''}
      </Tag>
      {liters && (
        <Text fontSize="xs" color="orange.400">
          GesCooper: {formatLiters(check.erp_liters)} L
        </Text>
      )}
    </>
  );
}

// descargas anotadas no posto, conferidas com a entrada da nf no gescooper
export function ReceiptsHistory({
  receipts,
  checks,
  erpAvailable,
  onPhoto,
}: {
  receipts: FuelReceipt[];
  checks: FuelReceiptCheck[];
  erpAvailable: boolean;
  onPhoto: (receipt: FuelReceipt) => void;
}) {
  const queryClient = useQueryClient();
  const [canceling, setCanceling] = useState<FuelReceipt | null>(null);

  if (receipts.length === 0) {
    return (
      <Text color="gray.500">
        Nenhum recebimento anotado no mês. O frentista anota a nota fiscal pelo
        celular quando o caminhão descarrega.
      </Text>
    );
  }

  const cancel = async (reason: string) => {
    await fuelReadingsService.cancelReceipt(
      (canceling as FuelReceipt).id,
      reason,
    );
    await queryClient.invalidateQueries('fuel-readings');
  };

  return (
    <Box overflowX="auto" bg="gray.800" borderRadius="12px" p="2">
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Descarga</Th>
            <Th>Nº NF</Th>
            <Th isNumeric>Litros</Th>
            <Th>GesCooper</Th>
            <Th>Lançado por</Th>
            <Th>Foto</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {receipts.map(item => (
            <Tr key={item.id} opacity={item.canceled_at ? 0.55 : 1}>
              <Td whiteSpace="nowrap">{formatDay(item.received_on)}</Td>
              <Td fontFamily="mono">{item.document}</Td>
              <Td isNumeric>{formatLiters(item.liters)}</Td>
              <Td>
                {!item.canceled_at && (
                  <ReceiptStatus
                    check={checks.find(check => check.id === item.id)}
                    erpAvailable={erpAvailable}
                  />
                )}
              </Td>
              <Td>
                {item.access_name || '—'}
                <Text fontSize="xs" color="gray.500">
                  anotado {formatDateTime(item.created_at)}
                </Text>
                {item.notes && (
                  <Text fontSize="xs" color="gray.500">
                    {item.notes}
                  </Text>
                )}
              </Td>
              <Td>
                <Thumb
                  item={item}
                  kind="receipt"
                  alt={`Foto da NF ${item.document}`}
                  onClick={() => onPhoto(item)}
                />
              </Td>
              <Td>
                {item.canceled_at ? (
                  <Canceled item={item} />
                ) : (
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="red"
                    onClick={() => setCanceling(item)}
                  >
                    Anular
                  </Button>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <CancelDialog
        title="Anular recebimento"
        isOpen={!!canceling}
        onClose={() => setCanceling(null)}
        onConfirm={cancel}
      >
        {canceling && (
          <Text>
            NF <b>{canceling.document}</b>, {formatLiters(canceling.liters)} L,
            descarga em {formatDay(canceling.received_on)}.
          </Text>
        )}
      </CancelDialog>
    </Box>
  );
}
